/* eslint-disable @typescript-eslint/no-explicit-any */
import { diagnosticReportUUID, boomWorkflow } from '@/constants';
import { removeNullAndEmptyObjects, removeTypeName } from '@/lib/utils';
import { v4 as uuidv4 } from 'uuid';
import { CommunicationRequestType } from '@/types';

interface Parameter {
  system: string;
  code: string;
  display: string;
}

interface LabRecord {
  id: number;
  parameter: Parameter;
  value: string;
  lowerRange: string;
  upperRange: string;
  unit: string;
  approved: boolean;
  effectiveDateTime: string;
}

interface FHIRResource {
  resourceType: string;
  [key: string]: any;
}

interface BundleEntry {
  fullUrl?: string;
  resource: FHIRResource;
  request: {
    method: 'POST' | 'PUT';
    url: string;
  };
}

const createObservation = (
  record: LabRecord,
  patientReference: string,
  effectiveDateTime: string
): BundleEntry => {
  const observationUUID = uuidv4();
  const fullUrl = `urn:uuid:${observationUUID}`;

  const numericValue = parseFloat(record.value);
  const isNumeric = !isNaN(numericValue) && isFinite(numericValue);

  return {
    fullUrl,
    resource: {
      resourceType: 'Observation',
      status: 'final',
      code: { text: record.parameter.display, coding: [record.parameter] },
      component: [{ code: { coding: [record.parameter] } }],
      ...(record.value
        ? isNumeric
          ? {
              valueQuantity: {
                value: numericValue,
                unit: record.unit || '',
                system: record.parameter.system,
                code: record.parameter.code,
              },
            }
          : {
              valueString: record.value,
            }
        : {}),
      referenceRange:
        record.lowerRange || record.upperRange
          ? [
              {
                ...(record.lowerRange && {
                  low: {
                    value: parseFloat(record.lowerRange),
                    unit: record.unit || '',
                    system: record.parameter.system,
                    code: record.parameter.code,
                  },
                }),
                ...(record.upperRange && {
                  high: {
                    value: parseFloat(record.upperRange),
                    unit: record.unit || '',
                    system: record.parameter.system,
                    code: record.parameter.code,
                  },
                }),
              },
            ]
          : [],
      subject: { reference: patientReference },
      effectiveDateTime: effectiveDateTime,
    },
    request: {
      method: 'POST',
      url: 'Observation',
    },
  };
};

const createDiagnosticReport = (
  observations: BundleEntry[],
  patientReference: string,
  performerReference: string,
  practitionerReference: string,
  effectiveDateTime: string
): BundleEntry => {
  return {
    fullUrl: diagnosticReportUUID,
    resource: {
      resourceType: 'DiagnosticReport',
      status: 'final',
      code: { text: 'Lab Test Report' },
      subject: { reference: patientReference },
      effectiveDateTime: effectiveDateTime,
      issued: effectiveDateTime,
      performer: [{ reference: performerReference }],
      resultsInterpreter: [{ reference: practitionerReference }],
      result: observations.map((obs) => ({ reference: obs.fullUrl })),
    },
    request: {
      method: 'POST',
      url: 'DiagnosticReport',
    },
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createTaskUpdate = (
  status: string,
  businessStatus: string,
  taskDetails: any,
  ownerReference: string,
  output?: any,
  displayName?: string
): BundleEntry => {
  const updatedTaskDetails = {
    ...taskDetails,
    focus: { reference: taskDetails.focus?.reference },
    for: { reference: taskDetails.for?.reference },
    owner: {
      reference: `${ownerReference}`,
      display: displayName || taskDetails.owner?.display || '',
    },
    status,
    businessStatus: { coding: [{ code: businessStatus }] },
    ...(output && {
      output: [
        {
          type: {
            coding: [
              {
                code: output.code,
                display: output.reason,
              },
            ],
          },
          valueString: output.description,
        },
      ],
    }),
  };

  const taskData = removeNullAndEmptyObjects(
    removeTypeName(updatedTaskDetails)
  );

  return {
    resource: {
      resourceType: 'Task',
      ...taskData,
    },
    request: {
      method: 'PUT',
      url: `Task/${taskDetails.id}`,
    },
  };
};

const createDocumentReference = (
  documentReferenceWithoutTypenameAndNulls: any,
  status: string,
  docStatus: string
): BundleEntry => {
  documentReferenceWithoutTypenameAndNulls.docStatus = docStatus;
  documentReferenceWithoutTypenameAndNulls.status = status;
  documentReferenceWithoutTypenameAndNulls.resourceType = 'DocumentReference';

  let newRelated: { reference: string; }[] = [];
  if (status === "final") {
    newRelated = [{ reference: diagnosticReportUUID }];
  }
  
  // Ensure context exists before modifying
  documentReferenceWithoutTypenameAndNulls.context = {
    ...documentReferenceWithoutTypenameAndNulls.context,
    ...(newRelated.length > 0 && {
      related: [
        ...(documentReferenceWithoutTypenameAndNulls.context?.related || []),
        ...newRelated
      ]
    })
  };

  return {
    resource: documentReferenceWithoutTypenameAndNulls,
    request: {
      method: 'PUT',
      url: `DocumentReference/${documentReferenceWithoutTypenameAndNulls.id}`,
    },
  };
};

export function createCommunicationRequest(params: {
  patientId: string;
  status?: string;
  content: string;
  template?: string;
  mediumCode: 'Communication-EMAIL' | 'Communication-APP_PUSH';
  mediumDisplay: string;
  noteText?: string;
  occurrenceDateTime?: string;
}): CommunicationRequestType {
  const {
    patientId,
    status = 'draft',
    content,
    template,
    mediumCode,
    mediumDisplay,
    noteText,
    occurrenceDateTime,
  } = params;

  const payloadContent = template
    ? JSON.stringify({ template, Reason1: content })
    : content;

  const communicationRequest: CommunicationRequestType = {
    resourceType: 'CommunicationRequest',
    status,
    subject: {
      reference: `${patientId}`,
    },
    payload: [
      {
        contentString: payloadContent,
      },
    ],
    category: [
      {
        coding: [
          {
            system:
              'http://terminology.hl7.org/CodeSystem/communication-category',
            code: 'notification',
            display: 'Notification',
          },
        ],
      },
    ],
    medium: [
      {
        coding: [
          {
            system: 'https://fluentinhealth.com/FHIR/CodeSystem/FluentHealthUI',
            code: mediumCode,
            display: mediumDisplay,
          },
        ],
      },
    ],
    ...(noteText && {
      note: [
        {
          text: noteText,
        },
      ],
    }),
    ...(occurrenceDateTime && { occurrenceDateTime }),
  };

  return communicationRequest;
}

// export const createAuditEvent = (eventType: string, taskDetails: any, practitionerReference: string, isSupervisor?: boolean): BundleEntry => ({
//   resource: {
//     resourceType: "AuditEvent",
//     type: { system: "http://terminology.hl7.org/CodeSystem/iso-21089-lifecycle", code: eventType },
//     action: "U",
//     recorded: new Date().toISOString(),
//     outcome: "0",
//     agent: [{
//       type: { coding: [{ system: "http://terminology.hl7.org/CodeSystem/contractsignertypecodes", code: isSupervisor ? "VERF"  : "VALID", display: isSupervisor ? "Verifier" : 'Validator' }] },
//       role: [{ coding: [{ system: "http://terminology.hl7.org/CodeSystem/contractsignertypecodes", code:  isSupervisor ? "VERF"  : "VALID" , display: isSupervisor ? "Verifier" : 'Validator' }] }],
//       who: { reference:  `Practitioner/${practitionerReference}` },
//       requestor: true,
//     }],
//     source: { observer: { reference: "Organization/FluentHealth" },
//     type: [
//       {
//         system: 'http://terminology.hl7.org/CodeSystem/audit-source-type',
//         code: 'fh:console-documentengine',
//         display: 'FH Console Document Management',
//       },
//     ], },
//     entity: [{ what: { reference: taskDetails.focus.reference } }],
//   },
//   request: { method: "POST", url: "AuditEvent" },
// });

//TODO: Need to manage conditional update of task business status

const createBundleForApproved = (
  parameters: LabRecord[],
  patientReference: string,
  performerReference: string,
  practitionerReference: string,
  taskDetails: any,
  isSupervisor?: boolean,
  practitionerDisplayName?: string
): BundleEntry[] => {
  const documentReference = taskDetails?.focus?.resource;
  const documentReferenceWithoutTypename = removeTypeName(documentReference);
  const documentReferenceWithoutTypenameAndNulls = removeNullAndEmptyObjects(
    documentReferenceWithoutTypename
  );
  const effectiveDateTime = documentReferenceWithoutTypenameAndNulls?.context?.period?.start || new Date().toISOString();
  const observations = parameters.map((record) =>
    createObservation(record, patientReference, effectiveDateTime)
  );
  const emailRequest = createCommunicationRequest({
    patientId: taskDetails?.for?.reference ?? '',
    content: '',
    template: 'record-approved',
    mediumCode: 'Communication-EMAIL',
    mediumDisplay: 'Email Notification',
  });
  const pushRequest = createCommunicationRequest({
    patientId: taskDetails?.for?.reference ?? '',
    content: 'Your Document: Officially Healthy',
    mediumCode: 'Communication-APP_PUSH',
    mediumDisplay: 'App Push Notification',
    noteText:'Your health document has been successfully reviewed and approved! Return to the app to take advantage of personalised health content and guidance.',
    occurrenceDateTime: new Date().toISOString(),
  });
  return [
    ...observations,
    createDiagnosticReport(
      observations,
      patientReference,
      performerReference,
      practitionerReference, 
      effectiveDateTime
    ),
    createTaskUpdate(
      'completed',
      isSupervisor
        ? boomWorkflow?.boomSupervisor?.validationCompleted
        : boomWorkflow?.boomValidator?.completed,
      taskDetails,
      practitionerReference,
      null,
      practitionerDisplayName
    ),
    createDocumentReference(
      documentReferenceWithoutTypenameAndNulls,
      'final',
      'final'
    ),
    {
      resource: emailRequest,
      request: {
        method: 'POST',
        url: 'CommunicationRequest',
      },
    },
    {
      resource: pushRequest,
      request: {
        method: 'POST',
        url: 'CommunicationRequest',
      },
    },
  ];
};

const createBundleForRejected = (
  patientReference: string,
  practitionerReference: string,
  taskDetails: any,
  output?: any,
  isSupervisior?: boolean,
  practitionerDisplayName?: string
): BundleEntry[] => {
  const documentReference = taskDetails?.focus?.resource;
  const documentReferenceWithoutTypename = removeTypeName(documentReference);
  const documentReferenceWithoutTypenameAndNulls = removeNullAndEmptyObjects(
    documentReferenceWithoutTypename
  );
  const emailRequest = createCommunicationRequest({
    patientId: taskDetails?.for?.reference ?? '',
    content: '',
    template: 'records-rejected',
    mediumCode: 'Communication-EMAIL',
    mediumDisplay: 'Email Notification',
  });
  const pushRequest = createCommunicationRequest({
    patientId: taskDetails?.for?.reference ?? '',
    content: 'recorded-rejected',
    mediumCode: 'Communication-APP_PUSH',
    mediumDisplay:
      "Unfortunately, your health document wasn't approved. We want to help you. Please check your email to see what to do next. Don't worry, we will figure it out together. ",
    noteText: 'Document Review Failed',
    occurrenceDateTime: new Date().toISOString(),
  });
  return [
    createDocumentReference(
      documentReferenceWithoutTypenameAndNulls,
      'entered-in-error',
      'entered-in-error'
    ),
    createTaskUpdate(
      'rejected',
      isSupervisior
        ? boomWorkflow?.boomSupervisor?.validationRejected
        : boomWorkflow?.boomValidator?.rejected,
      taskDetails,
      practitionerReference,
      output,
      practitionerDisplayName
    ),
    {
      resource: emailRequest,
      request: {
        method: 'POST',
        url: 'CommunicationRequest',
      },
    },
    {
      resource: pushRequest,
      request: {
        method: 'POST',
        url: 'CommunicationRequest',
      },
    },
  ];
};

const createBundleForEscalated = (
  patientReference: string,
  practitionerReference: string,
  taskDetails: any,
  output?: any,
  isSupervisior?: boolean,
  practitionerDisplayName?: string
): BundleEntry[] => {
  // const documentReference = taskDetails?.focus?.resource;
  // const documentReferenceWithoutTypename = removeTypeName(documentReference);
  // const documentReferenceWithoutTypenameAndNulls = removeNullAndEmptyObjects(documentReferenceWithoutTypename);
  return [
    // createDocumentReference(documentReferenceWithoutTypenameAndNulls, "entered-in-error", "entered-in-error"),
    createTaskUpdate(
      'completed',
      isSupervisior
        ? boomWorkflow?.boomSupervisor?.validationEscalated
        : boomWorkflow?.boomValidator?.escalated,
      taskDetails,
      practitionerReference,
      output,
      practitionerDisplayName
    ),
    // createAuditEvent("amend", taskDetails, practitionerReference, isSupervisior),
  ];
};

const createBundleForWorkflowCompleted = (
  taskDetails: any,
  status: string
): BundleEntry[] => {
  return [
    createTaskUpdate(
      status,
      'document-engine:workflow:process:completed',
      taskDetails,
      taskDetails.for.reference
    ),
  ];
};

export function createCommunicationRequestPayload({
  type,
  subjectReference,
  template,
  output,
}: {
  type: 'EMAIL' | 'APP_PUSH';
  subjectReference: string;
  template: string;
  output?: { reason?: string };
}): CommunicationRequestType {
  if (type === 'EMAIL') {
    // For reject, include reason; for approve, just template
    const contentObj: any = { template };
    if (template === 'records-rejected' && output?.reason) {
      contentObj.Reason1 = output.reason;
    }
    return {
      status: 'draft',
      subject: { reference: subjectReference },
      payload: [
        {
          contentString: JSON.stringify(contentObj),
        },
      ],
      category: [
        {
          coding: [
            {
              system: 'http://terminology.hl7.org/CodeSystem/communication-category',
              code: 'notification',
              display: 'Notification',
            },
          ],
        },
      ],
      medium: [
        {
          coding: [
            {
              system: 'https://fluentinhealth.com/FHIR/CodeSystem/FluentHealthUI',
              code: 'Communication-EMAIL',
              display: 'Email Notification',
            },
          ],
        },
      ],
      resourceType: 'CommunicationRequest',
    };
  } else {
    // For APP_PUSH, you can further customize if needed
    return {
      note: [
        {
          text:
            template === 'records-rejected'
              ? "Unfortunately, your health document wasn't approved. We want to help you. Please check your email to see what to do next. Don’t worry, we will figure it out together."
              : 'Your health document has been successfully reviewed and approved! Return to the app to take advantage of personalised health content and guidance.',
        },
      ],
      payload: [
        {
          contentString:
            template === 'records-rejected'
              ? 'Document Review Failed'
              : 'Your Document: Officially Healthy',
        },
      ],
      status: 'draft',
      resourceType: 'CommunicationRequest',
      category: [
        {
          coding: [
            {
              system:
                'http://terminology.hl7.org/CodeSystem/communication-category',
              code: 'notification',
              display: 'Notification',
            },
          ],
        },
      ],
      medium: [
        {
          coding: [
            {
              system:
                'https://fluentinhealth.com/FHIR/CodeSystem/FluentHealthUI',
              code: 'Communication-APP_PUSH',
              display: 'App Push Notification',
            },
          ],
        },
      ],
      occurrenceDateTime: new Date().toISOString(),
      subject: { reference: subjectReference },
    };
  }
}


export { createBundleForApproved, createBundleForRejected, createBundleForEscalated, createBundleForWorkflowCompleted };
